import React from 'react'
import PropTypes from 'prop-types'
import { HeaderRenderer } from 'react-data-grid'

import { numberWithThousandsSeparators } from 'utils/helpers'
import ColumnHeaderWithAggregatedValue from './ColumnHeaderWithAggregatedValue'

const RightAlignedHeader = ({ column }) => {
  return (
    <div className="ml-auto">
      <HeaderRenderer column={column} />
    </div>
  )
}

RightAlignedHeader.propTypes = {
  column: PropTypes.object.isRequired,
}

const customFormatter = ({ row, column, isRowSelected, onRowChange }, t) => {
  if (column.customFormatter) {
    return column.customFormatter({ row, column, isRowSelected, onRowChange }, t) ?? ''
  }

  const tKey = `Sentinel.results.${column.entity}.${row[column.key]?.toString() ?? ''}`
  return t([tKey, row[column.key]?.toString() ?? ''])
}

// format strings like "rock_pop" into "Rock Pop"
const formatString = (value) => {
  if (!value) return ''
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const formatDateString = (dateStr) => {
  if (!dateStr) {
    return '-'
  }
  return dateStr.split(' ')[0]
}

export const userColumns = [
  { entity: 'customers', key: 'id', name: 'ID' },
  { entity: 'customers', key: 'salutation', name: 'Salutation', minWidth: 140 },
  { entity: 'customers', key: 'first_name', name: 'First Name', minWidth: 140 },
  { entity: 'customers', key: 'last_name', name: 'Last Name', minWidth: 140 },
  { entity: 'customers', key: 'street', name: 'Street' },
  { entity: 'customers', key: 'zip_code', name: 'Zip Code', minWidth: 140 },
  { entity: 'customers', key: 'city', name: 'City' },
  { entity: 'customers', key: 'email', name: 'Email' },
  { entity: 'customers', key: 'email_2', name: 'Email 2' },
  { entity: 'customers', key: 'phone1', name: 'Phone' },
  { entity: 'customers', key: 'phone2', name: 'Phone 2' },
  {
    entity: 'customers',
    key: 'ads_opt_out',
    name: 'Ads Opt Out',
    minWidth: 150,
  },
]

export const eventColumns = [
  { entity: 'events', key: 'id', name: 'ID' },
  { entity: 'events', key: 'title', name: 'Title' },
  { entity: 'events', key: 'description', name: 'Description', minWidth: 140 },
  {
    entity: 'events',
    key: 'category',
    name: 'Category',
    customFormatter: ({ row, column }) => {
      return formatString(row[column.key])
    },
  },
  { entity: 'events', key: 'date', name: 'Date' },
  {
    entity: 'events',
    key: 'start',
    name: 'Start',
    customFormatter: ({ row, column }) => {
      return row[column.key]?.slice(0, 5)
    },
  },
  {
    entity: 'events',
    key: 'end',
    name: 'End',
    customFormatter: ({ row, column }) => {
      return row[column.key]?.slice(0, 5)
    },
  },
  {
    entity: 'events',
    key: 'capacity',
    name: 'Capacity',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
  },
  {
    entity: 'events',
    key: 'price_category',
    name: 'Price Category',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    minWidth: 140,
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
  },
  { entity: 'events', key: 'city', name: 'City' },
  {
    entity: 'events',
    key: 'venue',
    name: 'Venue',
    minWidth: 140,
    customFormatter: ({ row }) => {
      if (!row.venues?.length) {
        return ''
      }

      return row.venues
    },
  },
  {
    entity: 'events',
    key: 'hall',
    name: 'Hall',
    minWidth: 140,
    customFormatter: ({ row }) => {
      if (!row.halls?.length) {
        return ''
      }

      return row.halls
    },
  },
  {
    entity: 'events',
    key: 'artist_name',
    name: 'Artist',
    customFormatter: ({ row }) => {
      return row.lineups?.map((artist) => artist.artist_name).join(' | ') || ''
    },
    minWidth: 140,
  },
  {
    entity: 'events',
    key: 'composer',
    name: 'Author',
    customFormatter: ({ row }) => {
      return row.setlists?.map((composer) => composer.composer).join(' | ') || ''
    },
    minWidth: 140,
  },
  {
    entity: 'events',
    key: 'composer_title',
    name: 'Setlist',
    customFormatter: ({ row }) => {
      return row.setlists?.map((composer) => composer.title).join(' | ') || ''
    },
    minWidth: 140,
  },
  {
    entity: 'events',
    key: 'already_sold',
    name: 'Already sold',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key], { suffix: '%' })
    },
    headerRenderer: RightAlignedHeader,
    minWidth: 120,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    incompatibleWithPrisma: true,
  },
  {
    entity: 'events',
    key: 'prediction',
    name: 'Prediction',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key], { suffix: '%' })
    },
    headerRenderer: RightAlignedHeader,
    minWidth: 120,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    incompatibleWithPrisma: true,
  },
  {
    entity: 'events',
    key: 'sales_prediction',
    name: 'Sales prediction',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key], { suffix: '%' })
    },
    headerRenderer: RightAlignedHeader,
    minWidth: 120,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    incompatibleWithPrisma: true,
  },
  {
    entity: 'events',
    key: 'benchmark',
    name: 'Benchmark',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key], { suffix: '%' })
    },
    headerRenderer: RightAlignedHeader,
    cellClass: 'text-right pr-4',
    headerCellClass: 'text-right pr-4',
    incompatibleWithPrisma: true,
  },
]

export const transactionColumns = [
  { entity: 'transactions', key: 'id', name: 'ID' },
  { entity: 'transactions', key: 'event_id', name: 'Event ID' },
  { entity: 'transactions', key: 'customer_id', name: 'Customer ID', minWidth: 100 },
  { entity: 'transactions', key: 'invoice_id', name: 'Invoice ID', minWidth: 140 },
  {
    entity: 'transactions',
    key: 'ticket_type',
    name: 'Ticket Type',
    minWidth: 100,
    customFormatter: ({ row, column }) => {
      return formatString(row[column.key])
    },
  },
  { entity: 'transactions', key: 'date', name: 'Date' },
  {
    entity: 'transactions',
    key: 'time',
    name: 'Time',
    customFormatter: ({ row, column }) => {
      return row[column.key]?.slice(0, 5)
    },
  },
  {
    entity: 'transactions',
    key: 'sold_price',
    name: 'Sold Price',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    minWidth: 120,
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
  },
  {
    entity: 'transactions',
    key: 'ground_price',
    name: 'Ground Price',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    minWidth: 120,
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
  },
  {
    entity: 'transactions',
    key: 'discount',
    name: 'Discount',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
  },
  { entity: 'transactions', key: 'ticket_category', name: 'FD Ticket Category', minWidth: 150 },
  {
    entity: 'transactions',
    key: 'raw_ticket_category',
    name: 'Client ticket category',
    minWidth: 150,
  },
  { entity: 'transactions', key: 'currency', name: 'Currency' },
  { entity: 'transactions', key: 'payment_method', name: 'Payment Method', minWidth: 150 },
  { entity: 'transactions', key: 'seat_id', name: 'Seat ID' },
]

export const campaignColumns = [
  { entity: 'campaigns', key: 'id', name: 'Campaign ID', minWidth: 180 },
  { entity: 'campaigns', key: 'eid', name: 'Event ID', minWidth: 150 },
  { entity: 'campaigns', key: 'event_title', name: 'Title', minWidth: 200 },
  {
    entity: 'campaigns',
    key: 'event_date',
    name: 'Event date',
    minWidth: 160,
    customFormatter: ({ row, column }) => {
      return formatDateString(row[column.key])
    },
  },
  {
    entity: 'campaigns',
    key: 'campaign_start_date',
    name: 'Start Date',
    minWidth: 180,
    customFormatter: ({ row, column }) => {
      return formatDateString(row[column.key])
    },
  },
  {
    entity: 'campaigns',
    key: 'campaign_stop_date',
    name: 'Stop Date',
    minWidth: 180,
    customFormatter: ({ row, column }) => {
      return formatDateString(row[column.key])
    },
  },
  {
    entity: 'campaigns',
    key: 'spend',
    name: 'Ad Spend',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
  },
  {
    entity: 'campaigns',
    key: 'impressions',
    name: 'Impressions',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    minWidth: 120,
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
  },
  {
    entity: 'campaigns',
    key: 'clicks',
    name: 'Clicks',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
  },
  {
    entity: 'campaigns',
    key: 'reach',
    name: 'Reach',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
  },
  {
    entity: 'campaigns',
    key: 'total_number_of_ads',
    name: 'Number of Ads',
    minWidth: 120,
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
  },
  {
    entity: 'campaigns',
    key: 'total_taste_clusters',
    name: 'Number of TC',
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
    minWidth: 200,
  },
  {
    entity: 'campaigns',
    key: 'attribution_model',
    name: 'Attribution model',
    minWidth: 180,
  },
  {
    entity: 'campaigns',
    key: 'purchase_single',
    name: 'Ticket (Single Event)',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
    minWidth: 150,
  },
  {
    entity: 'campaigns',
    key: 'revenue_single',
    name: 'Revenue (Single Event)',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
    minWidth: 180,
  },
  {
    entity: 'campaigns',
    key: 'cpo_single',
    name: 'Cost per Order (Single Event)',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    headerRenderer: RightAlignedHeader,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    minWidth: 200,
  },
  {
    entity: 'campaigns',
    key: 'purchase_series',
    name: 'Ticket (Event Series)',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
    minWidth: 150,
  },
  {
    entity: 'campaigns',
    key: 'revenue_series',
    name: 'Revenue (Event Series)',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
    minWidth: 150,
  },
  {
    entity: 'campaigns',
    key: 'cpo_series',
    name: 'Cost per Order (Event Series)',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    headerRenderer: RightAlignedHeader,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    minWidth: 180,
  },
  {
    entity: 'campaigns',
    key: 'roi',
    name: 'ROI',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key], { suffix: '%' })
    },
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
    minWidth: 150,
  },
  {
    entity: 'campaigns',
    key: 'total_revenue',
    name: 'Total Revenue',
    customFormatter: ({ row, column }) => {
      return numberWithThousandsSeparators(row[column.key])
    },
    headerRenderer: ColumnHeaderWithAggregatedValue,
    cellClass: 'text-right',
    headerCellClass: 'text-right',
    aggregatedCellClass: 'ml-auto',
    minWidth: 160,
  },
  {
    entity: 'campaigns',
    key: 'event_series',
    name: 'Event Series',
    customFormatter: ({ row, column }) => {
      if (!row[column.key]?.length) {
        return '-'
      }
      // if not a list, return the value directly
      if (typeof row[column.key] !== 'object') {
        return row[column.key]
      }

      return row[column.key]?.join(', ') || ''
    },
    cellClass: 'pl-3',
    headerCellClass: 'pl-3',
    minWidth: 200,
  },
]

export const getColumns = (t, hasPrismaAccess) => {
  const eventCols = !hasPrismaAccess
    ? eventColumns
    : eventColumns.filter((column) => !column.incompatibleWithPrisma)

  return {
    customers: userColumns.map((column) => {
      return {
        ...column,
        name: t([`Sentinel.results.customers.columns.${column.key}`, column.name]),
        formatter: (data) => customFormatter(data, t),
      }
    }),
    transactions: transactionColumns.map((column) => ({
      ...column,
      name: t([`Sentinel.results.transactions.columns.${column.key}`, column.name]),
      formatter: (data) => customFormatter(data, t),
    })),
    events: eventCols.map((column) => ({
      ...column,
      name: t([`Sentinel.results.events.columns.${column.key}`, column.name]),
      formatter: (data) => customFormatter(data, t),
    })),
    campaigns: campaignColumns.map((column) => ({
      ...column,
      name: t([`Sentinel.results.campaigns.columns.${column.key}`, column.name]),
      formatter: (data) => customFormatter(data, t),
    })),
  }
}

export const entityHasAggregates = (entity) => ['transactions', 'events', 'campaigns'].includes(entity)
